import { useAuth0 } from '@auth0/auth0-react';
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  RawAxiosRequestHeaders,
} from 'axios';
import axiosRetry from 'axios-retry';
import toast from 'react-hot-toast';
import { getApiBaseUrl } from './apiBaseUrl';
import { serverErrorMessage } from './serverErrorMessage';

/**
 * Thrown by callProtectedApi when Auth0 reports the user unauthenticated —
 * a routine condition during logout/auth transitions, not a defect. Typed so
 * the global query/mutation error reporting (reportApiError.ts) can exclude
 * it by instanceof instead of matching message strings.
 */
export class NotAuthenticatedError extends Error {
  constructor() {
    super('User is not authenticated');
    this.name = 'NotAuthenticatedError';
  }
}

/**
 * A genuine failure to obtain an Auth0 access token — an outage, timeout, or
 * network drop reaching Auth0 (NOT a routine session lapse, which is
 * NotAuthenticatedError). Distinct type + message so it reports as its own
 * Sentry issue instead of masquerading as an API network failure, with the
 * underlying Auth0 error chained via `cause`.
 */
export class AuthTokenError extends Error {
  constructor(cause: unknown) {
    super('Failed to acquire Auth0 access token');
    this.name = 'AuthTokenError';
    // Assigned via cast: Error.cause needs lib es2022, but the runtime (and
    // Sentry's exception chaining) reads it regardless of the ts target.
    (this as { cause?: unknown }).cause = cause;
  }
}

// Auth0 OAuth error codes that mean "the session has ended, the user must
// re-authenticate" — routine, not an outage. A getAccessTokenSilently
// rejection carrying one of these is mapped to NotAuthenticatedError (filtered
// from Sentry). A rejection WITHOUT one of these (network drop, Auth0 timeout,
// 5xx from the token endpoint) is a real failure and is left reportable.
const OAUTH_SESSION_LAPSE_CODES = new Set([
  'login_required',
  'consent_required',
  'mfa_required',
  'missing_refresh_token',
]);

const isAuthSessionLapse = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false;
  const code = (error as { error?: unknown }).error;
  return typeof code === 'string' && OAUTH_SESSION_LAPSE_CODES.has(code);
};

// Scoped axios instance. axios-retry attaches interceptors to whatever
// instance it is given, so the previous code — which re-applied retry config
// to the global default `axios` singleton inside the request path on every
// call — re-registered interceptors on each request and leaked retry behavior
// across the whole app. We create one instance here and attach the retry
// interceptor exactly ONCE, at module load. The per-call `retries` count is
// applied per request via the `axios-retry` config key below, so the
// stacking/mutation bug is gone while the retry-on-500 behavior is preserved.
const apiClient = axios.create();
axiosRetry(apiClient, {
  retries: 0,
  retryDelay: (retryCount) => {
    return retryCount * 2000; // time interval between retries
  },
  retryCondition: (error) => {
    // if retry condition is not specified, by default idempotent requests are retried
    return error?.response?.status === 500;
  },
});

// Value sent as the X-App-Client header on client-tagged writes so the
// backend can attribute signups by channel; it normalizes `tach-web` → the
// `web` channel and stores it on Event.metadata.client and the customer's
// signup channel. See http-utils extractClientContext and the
// services/applications and services/customers CORS allow-lists.
const TACH_WEB_CLIENT = 'tach-web';

// True only for the three client-tagged WRITE routes — POST /applications/upsert,
// PATCH /applications/{id} and POST /customers (exact path). The list mirrors
// the backend CORS allow-lists: only those endpoints accept X-App-Client, so
// the header must not be attached to reads, to other /customers calls, or to
// POST /vendors and /partners (their services' CORS do not allow it) or the
// browser preflight would fail.
const targetsClientTaggedWrite = (
  requestType: string,
  url: string
): boolean => {
  const method = requestType.toLowerCase();
  return (
    url.startsWith('/applications/upsert') ||
    (method === 'patch' && url.startsWith('/applications/')) ||
    (method === 'post' && url === '/customers')
  );
};

interface RequestArgs {
  isProtected: boolean;
  url: string;
  requestType: string;
  body?: unknown;
  errorMsg?: string;
  newHeaders?: Record<string, string | undefined>;
  domain?: string;
  retries?: number;
  hideAlert?: boolean;
  /**
   * Opt-in, per call: when the failed response carries a `message`, show that
   * instead of the caller's generic `errorMsg`. Off by default so toast copy
   * for every other flow in the app is untouched — a generic-but-safe message
   * beats leaking whatever text an arbitrary endpoint happens to return.
   *
   * Reachable only through the options form of callProtectedApi — deliberately.
   * The positional form is eight arguments deep already, and a ninth would have
   * every opted-in call site padding three `undefined`s to reach it.
   */
  preferServerErrorMessage?: boolean;
}

/** Every field callProtectedApi accepts, named. See the overload below. */
export type ProtectedRequestOptions = Omit<RequestArgs, 'isProtected'>;

const useApi = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  // Shared base-URL resolution (dev proxy / staging / prod) — see apiBaseUrl.ts.
  const domainTach = getApiBaseUrl();

  const renderToast = (status: number | undefined, errorMsg: string) => {
    switch (status) {
      case 401:
        toast.error(
          `${errorMsg} Please email support@tachusa.com if this issue persists. [${status.toString()}]`
        );
        break;
      case 403:
        toast.error(
          `${errorMsg} Please email support@tachusa.com if this issue persists. [${status.toString()}]`
        );
        break;
      case 400:
        toast.error(
          `${errorMsg} Please email support@tachusa.com if this issue persists. [${status.toString()}]`
        );
        break;
      case 404:
        toast.error(
          `${errorMsg} Please email support@tachusa.com if this issue persists. [${status.toString()}]`
        );
        break;
      case 500:
        toast.error(
          `${errorMsg} Please email support@tachusa.com if this issue persists. [${status.toString()}]`
        );
        break;
    }
  };

  // Single request implementation shared by callApi/callProtectedApi. The only
  // difference between the two public methods is whether an auth token is
  // injected, so that is the one branch here; everything else (header merge,
  // method switch, per-call retries, toast-on-error) is identical.
  const request = async <T = any,>({
    isProtected,
    url,
    requestType,
    body = null,
    errorMsg,
    newHeaders,
    domain,
    retries = 0,
    hideAlert,
    preferServerErrorMessage,
  }: RequestArgs): Promise<AxiosResponse<T> | null> => {
    // Fetch the token in its own try so a token failure is distinguishable
    // from a request failure. A session lapse → filtered NotAuthenticatedError;
    // a genuine Auth0 outage → AuthTokenError (distinct + reportable, original
    // chained) rather than being flattened to a null response that would look
    // like an ordinary API network failure.
    let token: string | undefined;
    if (isProtected) {
      try {
        token = await getAccessTokenSilently();
      } catch (tokenError) {
        if (isAuthSessionLapse(tokenError)) {
          throw new NotAuthenticatedError();
        }
        throw new AuthTokenError(tokenError);
      }
    }

    try {
      const defaultHeaders: RawAxiosRequestHeaders = {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods':
          'GET, POST, PATCH, PUT, DELETE, OPTIONS', // Allow these methods
        'Access-Control-Allow-Headers': 'Content-Type, Authorization', // Allow these headers
      };

      if (token) {
        defaultHeaders.Authorization = `Bearer ${token}`;
      }

      // Merge default headers with custom headers
      const headers = {
        ...defaultHeaders,
        ...newHeaders,
      } as RawAxiosRequestHeaders;

      // Tag application funnel-step writes and customer creation with the web
      // client so the backend records where the signup is happening (web vs.
      // native mobile). Scoped to the endpoints whose CORS allow-list
      // includes this header.
      if (targetsClientTaggedWrite(requestType, url)) {
        headers['X-App-Client'] = TACH_WEB_CLIENT;
      }

      const completeUrl = (domain ?? domainTach) + url;

      // Apply the per-call retry count on this request only; the retry
      // delay/condition live on the shared instance created above.
      const config: AxiosRequestConfig = {
        headers,
        'axios-retry': { retries },
      };

      let response: AxiosResponse<T>;
      switch (requestType.toLowerCase()) {
        case 'get':
          response = await apiClient.get<T>(completeUrl, config);
          break;
        case 'post':
          response = await apiClient.post<T>(completeUrl, body, config);
          break;
        case 'patch':
          response = await apiClient.patch<T>(completeUrl, body, config);
          break;
        case 'put':
          response = await apiClient.put<T>(completeUrl, body, config);
          break;
        case 'delete':
          response = await apiClient.delete<T>(completeUrl, {
            ...config,
            data: body,
          });
          break;
        default:
          throw new Error(`Unsupported request type: ${requestType}`);
      }
      return response;
    } catch (error) {
      // Request-level failure (token already obtained). Resolve with the error
      // response or null and never rethrow — callers expect AxiosResponse |
      // null, and the failure stays reportable via the unwrapApiResponse →
      // ApiError path. Sentry reporting is centralized in the QueryCache /
      // MutationCache onError hooks (reportApiError.ts), not done here.
      const axiosError = error as AxiosError<T>;
      console.error(axiosError?.message);
      if (errorMsg && !hideAlert) {
        // Opted-in callers show the API's own reason; everyone else keeps the
        // generic copy. renderToast itself is unchanged either way, so the
        // support-email suffix and the [status] tag stay put.
        const message =
          (preferServerErrorMessage
            ? serverErrorMessage(axiosError.response?.data)
            : undefined) ?? errorMsg;
        renderToast(axiosError.response?.status, message);
      }
      return axiosError.response ?? null;
    }
  };

  /**
   * Options form. Prefer it for anything past `errorMsg` — 53 call sites reach
   * `hideAlert` by padding `undefined, undefined, 0`, and this is the way out
   * of that without touching a single one of them.
   */
  async function callProtectedApi<T = any>(
    options: ProtectedRequestOptions
  ): Promise<AxiosResponse<T> | null>;
  /** Positional form, unchanged — every existing caller uses this one. */
  async function callProtectedApi<T = any>(
    url: string,
    requestType: string,
    body?: unknown,
    errorMsg?: string,
    newHeaders?: Record<string, string | undefined>,
    domain?: string,
    retries?: number,
    hideAlert?: boolean
  ): Promise<AxiosResponse<T> | null>;
  async function callProtectedApi<T = any>(
    urlOrOptions: string | ProtectedRequestOptions,
    requestType?: string,
    body: unknown = null,
    errorMsg?: string,
    newHeaders?: Record<string, string | undefined>,
    domain?: string,
    retries = 0,
    hideAlert?: boolean
  ): Promise<AxiosResponse<T> | null> {
    if (!isAuthenticated) {
      throw new NotAuthenticatedError();
    }

    const args: ProtectedRequestOptions =
      typeof urlOrOptions === 'string'
        ? {
            url: urlOrOptions,
            requestType: requestType as string,
            body,
            errorMsg,
            newHeaders,
            domain,
            retries,
            hideAlert,
          }
        : urlOrOptions;

    return request<T>({ isProtected: true, ...args });
  }

  const callApi = async <T = any,>(
    url: string,
    requestType: string,
    body: unknown = null,
    errorMsg?: string,
    newHeaders?: Record<string, string | undefined>,
    domain?: string,
    retries = 0
  ): Promise<AxiosResponse<T> | null> => {
    return request<T>({
      isProtected: false,
      url,
      requestType,
      body,
      errorMsg,
      newHeaders,
      domain,
      retries,
    });
  };

  return { callProtectedApi, callApi };
};

export default useApi;
